import { useEffect, useMemo, useState } from "react";
import { api } from "../api";

interface MediaFile {
  id: string;
  media_id: string;
  language: string;
  size: string;
  drive_file_id: string;
  file_name: string;
}

interface MediaRow {
  id: string;
  code: string;
  type: string;
  angle: string;
  stage: string;
  theme: string;
  hook: string;
  body: string;
  cta: string;
  term_name: string;
  job_created_at: string;
  files: MediaFile[];
}

function DriveLangLinks({ files, size }: { files: MediaFile[]; size: string }) {
  const sizeFiles = files.filter((f) => f.size === size);
  if (sizeFiles.length === 0) return <span className="text-zinc-300">—</span>;
  return (
    <div className="flex flex-wrap gap-1 justify-center">
      {sizeFiles.map((file) => (
        <a
          key={file.id}
          href={`https://drive.google.com/file/d/${file.drive_file_id}/view`}
          target="_blank"
          rel="noreferrer"
          className="inline-block px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-900 whitespace-nowrap"
          title={file.file_name}
        >
          {file.language}
        </a>
      ))}
    </div>
  );
}

export function Gallery({ productId }: { productId: string }) {
  const [rows, setRows] = useState<MediaRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterTheme, setFilterTheme] = useState("all");
  const [filterType, setFilterType] = useState("all");

  useEffect(() => {
    api.listMedia(productId).then((r) => {
      setRows(r.media as MediaRow[]);
      setLoading(false);
    });
  }, [productId]);

  const types = useMemo(
    () => Array.from(new Set(rows.map((r) => r.type))).filter(Boolean).sort(),
    [rows],
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter((r) => {
      if (filterTheme !== "all" && r.theme !== filterTheme) return false;
      if (filterType !== "all" && r.type !== filterType) return false;
      if (q) {
        return (
          r.hook?.toLowerCase().includes(q) ||
          r.angle?.toLowerCase().includes(q) ||
          r.cta?.toLowerCase().includes(q) ||
          r.type?.toLowerCase().includes(q) ||
          r.term_name?.toLowerCase().includes(q) ||
          r.code?.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [rows, search, filterTheme, filterType]);

  function fmt(d: string) {
    return d ? d.slice(0, 10) : "—";
  }

  return (
    <div className="p-6 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h1 className="text-xl font-bold mr-2">Galeri</h1>
        <input
          type="text"
          placeholder="Ara…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border rounded px-3 py-1.5 text-sm w-52"
        />
        <select
          value={filterTheme}
          onChange={(e) => setFilterTheme(e.target.value)}
          className="border rounded px-2 py-1.5 text-sm"
        >
          <option value="all">Theme: hepsi</option>
          <option value="light">light</option>
          <option value="dark">dark</option>
        </select>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="border rounded px-2 py-1.5 text-sm"
        >
          <option value="all">Type: hepsi</option>
          {types.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Yükleniyor…</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-zinc-500">Kayıt bulunamadı.</div>
      ) : (
        <div className="overflow-auto flex-1 border rounded-lg">
          <table className="w-full text-xs border-collapse">
            <thead className="sticky top-0 bg-zinc-50 z-10">
              <tr>
                {[
                  { label: "Date",  cls: "w-24" },
                  { label: "Code",  cls: "w-20" },
                  { label: "Type",  cls: "w-28" },
                  { label: "Angle", cls: "" },
                  { label: "Stage", cls: "w-24" },
                  { label: "Theme", cls: "w-20" },
                  { label: "Hook",  cls: "" },
                  { label: "Body",  cls: "" },
                  { label: "CTA",   cls: "w-32" },
                  { label: "Term",  cls: "w-28" },
                  { label: "1:1",   cls: "w-16 text-center" },
                  { label: "9:16",  cls: "w-16 text-center" },
                ].map((col) => (
                  <th
                    key={col.label}
                    className={`text-left px-3 py-2 font-semibold text-zinc-700 border-b border-zinc-200 whitespace-nowrap ${col.cls}`}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((row, i) => (
                <tr key={row.id} className={i % 2 === 0 ? "bg-white" : "bg-zinc-50"}>
                  <td className="px-3 py-2 border-b border-zinc-100 whitespace-nowrap text-zinc-500">
                    {fmt(row.job_created_at)}
                  </td>
                  <td className="px-3 py-2 border-b border-zinc-100 whitespace-nowrap font-mono text-zinc-600">
                    {row.code}
                  </td>
                  <td className="px-3 py-2 border-b border-zinc-100 whitespace-nowrap text-zinc-800">
                    {row.type}
                  </td>
                  <td className="px-3 py-2 border-b border-zinc-100 max-w-xs truncate text-zinc-800" title={row.angle}>
                    {row.angle || "—"}
                  </td>
                  <td className="px-3 py-2 border-b border-zinc-100 whitespace-nowrap">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                      row.stage === "Unaware"
                        ? "bg-blue-100 text-blue-700"
                        : "bg-green-100 text-green-700"
                    }`}>
                      {row.stage || "—"}
                    </span>
                  </td>
                  <td className="px-3 py-2 border-b border-zinc-100 whitespace-nowrap">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                      row.theme === "dark"
                        ? "bg-zinc-800 text-white"
                        : "bg-zinc-100 text-zinc-700"
                    }`}>
                      {row.theme || "—"}
                    </span>
                  </td>
                  <td className="px-3 py-2 border-b border-zinc-100 max-w-xs truncate text-zinc-800" title={row.hook}>
                    {row.hook || "—"}
                  </td>
                  <td className="px-3 py-2 border-b border-zinc-100 max-w-xs truncate text-zinc-600" title={row.body}>
                    {row.body || "—"}
                  </td>
                  <td className="px-3 py-2 border-b border-zinc-100 whitespace-nowrap text-zinc-800">
                    {row.cta || "—"}
                  </td>
                  <td className="px-3 py-2 border-b border-zinc-100 whitespace-nowrap text-zinc-500">
                    {row.term_name || "—"}
                  </td>
                  <td className="px-3 py-2 border-b border-zinc-100 text-center">
                    <DriveLangLinks files={row.files} size="1:1" />
                  </td>
                  <td className="px-3 py-2 border-b border-zinc-100 text-center">
                    <DriveLangLinks files={row.files} size="9:16" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-2 text-xs text-zinc-400">{filtered.length} kayıt</div>
    </div>
  );
}
